import { useState } from "react";
import axios from "axios";
import { MapPin, Video, ShieldCheck, Lock, ArrowRight, Loader2, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSIONS = [
  { key: "session_in_person", label: "In-person session", where: "Marino, Dublin 3", price: "€60", icon: MapPin, testid: "pay-session-in-person" },
  { key: "session_online", label: "Online session", where: "Secure video, worldwide", price: "€50", icon: Video, testid: "pay-session-online" },
  { key: "custom_amount", label: "Agreed amount", where: "A fee agreed between you and Brian", price: "Custom", icon: HandCoins, testid: "pay-session-custom" },
];

const inputCls =
  "w-full rounded-xl border border-[#E2DACD] bg-white px-4 py-3 text-sm text-[#1E2723] placeholder:text-[#A8B4AD] focus:border-[#527363]";
const labelCls = "mb-1.5 block text-sm font-semibold text-[#2A3630]";

const Pay = () => {
  usePageTitle("Pay for a Session", "Securely pay for a psychotherapy session with Brian Boland — in person in Marino, Dublin 3 (€60) or online (€50).");
  const [sessionKey, setSessionKey] = useState("session_in_person");
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const isCustom = sessionKey === "custom_amount";
  const customValid = parseFloat(customAmount) >= 1 && parseFloat(customAmount) <= 1000;
  const buttonPrice = isCustom
    ? (customValid ? `€${parseFloat(customAmount).toFixed(2)}` : "enter amount")
    : SESSIONS.find((s) => s.key === sessionKey).price;

  const pay = async (e) => {
    e.preventDefault();
    if (isCustom && !customValid) {
      toast.error("Please enter an amount between €1 and €1,000.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: sessionKey,
        name,
        email,
        origin_url: window.location.origin,
        ...(isCustom ? { custom_amount: parseFloat(customAmount) } : {}),
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Could not start the secure payment. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="pay-page" className="mx-auto max-w-2xl px-5 pb-24 pt-16 sm:px-8 lg:pt-20">
      <Reveal y={12}><p className="eyebrow text-center">Secure payment</p></Reveal>
      <h1 className="mt-4 text-center font-serif text-4xl font-medium leading-[1.12] text-[#1E2723] sm:text-5xl">
        <MaskedLine delay={0.15}>Pay for a session</MaskedLine>
      </h1>
      <Reveal delay={0.35}>
        <p className="mx-auto mt-5 max-w-lg text-center text-base leading-relaxed text-[#4A5750]">
          Choose your session and enter your details — you'll be taken to Stripe's secure page to enter your card. Nothing is charged until you confirm there.
        </p>
      </Reveal>

      <Reveal delay={0.45}>
        <form onSubmit={pay} className="soft-card mt-10 space-y-6 p-7 sm:p-10" data-testid="pay-form">
          <fieldset>
            <legend className="mb-3 text-sm font-semibold text-[#2A3630]">Which session are you paying for?</legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {SESSIONS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={s.testid}
                  onClick={() => setSessionKey(s.key)}
                  aria-pressed={sessionKey === s.key}
                  className={`rounded-2xl border p-5 text-left transition-[background-color,border-color] duration-300 ${
                    sessionKey === s.key ? "border-[#527363] bg-[#E3ECE7]" : "border-[#E2DACD] bg-white hover:border-[#B5A793]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <s.icon size={18} className="text-[#527363]" />
                    <span className="font-serif text-2xl font-medium text-[#1E2723]">{s.price}</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-[#1E2723]">{s.label}</p>
                  <p className="text-xs text-[#65756C]">{s.where}</p>
                </button>
              ))}
            </div>
          </fieldset>

          {isCustom && (
            <div>
              <label htmlFor="pay-custom-amount" className={labelCls}>Agreed amount (€) *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#65756C]">€</span>
                <input
                  id="pay-custom-amount"
                  data-testid="pay-custom-amount-input"
                  type="number"
                  min="1"
                  max="1000"
                  step="0.01"
                  required
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className={`${inputCls} pl-8`}
                  placeholder="e.g. 45"
                />
              </div>
              <p className="mt-1.5 text-xs text-[#65756C]">Whatever you and Brian have agreed is appropriate — between €1 and €1,000.</p>
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="pay-name" className={labelCls}>Full name *</label>
              <input id="pay-name" data-testid="pay-name-input" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Your name" />
            </div>
            <div>
              <label htmlFor="pay-email" className={labelCls}>Email *</label>
              <input id="pay-email" data-testid="pay-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@example.ie" />
            </div>
          </div>

          <button type="submit" data-testid="pay-continue-button" disabled={loading} className="btn-primary w-full disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {loading ? "Opening secure payment…" : `Continue to secure payment · ${buttonPrice}`}
          </button>

          <p className="flex items-center justify-center gap-2 text-center text-xs text-[#65756C]">
            <ShieldCheck size={14} className="text-[#527363]" />
            Card details are handled entirely by Stripe — this website never sees or stores them.
          </p>
          <p className="text-center text-xs text-[#65756C]">
            After a successful payment, Stripe automatically emails you a receipt — nothing more to do.
          </p>
        </form>
      </Reveal>

      <Reveal delay={0.55}>
        <p className="mt-6 text-center text-sm text-[#54645B]">
          Haven't arranged a session yet?{" "}
          <a href="/booking" data-testid="pay-booking-link" className="font-semibold text-[#B85C43] underline decoration-[#B5A793] underline-offset-2">
            Send an enquiry first
          </a>{" "}
          — payment is usually made after your appointment is confirmed.
        </p>
      </Reveal>
    </div>
  );
};

export default Pay;
